// src/cockpit4/components/AIQuestionLibraryPanel.tsx
// ============================================
// AI 问题库（对应图3）
//
// 顶栏：AI问题库 标题 ｜ 右上角：批量取消收藏 / 批量收藏 / 批量监测（3 紫蓝描边按钮）
// 表头：复选框 □ ｜ AI问题  ⓘ  ｜ 问题类型 ｜ 热度值 ⓘ  ｜ 操作
// 空态：紫蓝开箱纸飞机 ｜ 「暂无AI问题」
// ============================================

import { useState, useMemo } from "react";
import {
  Bookmark, BookmarkCheck, PlayCircle, Info, ChevronDown,
} from "lucide-react";
import EmptyState from "./_MonitorEmptyState";

type QType = "产品选型" | "行业定义" | "落地案例" | "原理关系" | "对比评测" | "价格咨询" | "使用教程" | "售后服务";
const TYPE_COLOR: Record<QType, string> = {
  产品选型: "bg-violet-500/10 border border-violet-400/40 text-violet-300",
  行业定义: "bg-cyan-500/10 border border-cyan-400/40 text-cyan-300",
  落地案例: "bg-emerald-500/10 border border-emerald-400/40 text-emerald-300",
  原理关系: "bg-amber-500/10 border border-amber-400/40 text-amber-300",
  对比评测: "bg-fuchsia-500/10 border border-fuchsia-400/40 text-fuchsia-300",
  价格咨询: "bg-rose-500/10 border border-rose-400/40 text-rose-300",
  使用教程: "bg-indigo-500/10 border border-indigo-400/40 text-indigo-300",
  售后服务: "bg-slate-500/10 border border-slate-400/40 text-slate-300",
};

interface Q {
  id: string;
  question: string;
  type: QType;
  heat: number;
  saved: boolean;
  monitored: boolean;
}

const SAMPLES: Q[] = [];

export default function AIQuestionLibraryPanel({
  onAddToMonitor,
}:{ onAddToMonitor?: () => void }) {
  const [rows, setRows] = useState<Q[]>(SAMPLES);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const allChecked = rows.length > 0 && selected.size === rows.length;

  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };
  const toggleOne = (id:string) => {
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setSelected(ns);
  };

  const selRows = useMemo(()=>rows.filter(r=>selected.has(r.id)), [rows, selected]);

  const batchSave = (save: boolean) => {
    setRows(rows.map(r => selected.has(r.id) ? { ...r, saved: save } : r));
  };
  const batchMonitor = () => {
    setRows(rows.map(r => selected.has(r.id) ? { ...r, monitored: true } : r));
    onAddToMonitor?.();
  };

  return (
    <div className="px-4 py-4">
      {/* 顶栏 */}
      <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-3 border-b border-white/[0.06]">
        <div className="text-[15px] font-bold text-white flex items-center gap-2">
          <BookmarkCheck size={16} className="text-violet-400" />
          AI问题库
        </div>
        <div className="flex items-center gap-2">
          <button disabled={selected.size===0} onClick={()=>batchSave(false)}
            className="px-4 py-2 rounded-md text-[12.5px] border text-violet-300 border-violet-400/40 bg-violet-500/8 hover:bg-violet-500/15 hover:border-violet-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            批量取消收藏
          </button>
          <button disabled={selected.size===0} onClick={()=>batchSave(true)}
            className="px-4 py-2 rounded-md text-[12.5px] border text-violet-300 border-violet-400/40 bg-violet-500/8 hover:bg-violet-500/15 hover:border-violet-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            批量收藏
          </button>
          <button disabled={selected.size===0} onClick={batchMonitor}
            className="px-4 py-2 rounded-md text-[12.5px] font-semibold border text-violet-300 border-violet-400/40 bg-violet-500/8 hover:bg-violet-500/15 hover:border-violet-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            批量监测
          </button>
        </div>
      </div>

      {/* 表格卡 */}
      <div className="mt-4 rounded-2xl bg-white/[0.03] border border-white/[0.08] overflow-hidden">
        {rows.length === 0 ? (
          <EmptyState text="暂无AI问题" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr className="bg-white/[0.04] text-gray-400 text-[11.5px]">
                  <th className="text-left p-3 rounded-l-lg w-10">
                    <CheckBox checked={allChecked} onChange={toggleAll} />
                  </th>
                  <th className="text-left p-3">
                    <span className="inline-flex items-center gap-1.5">
                      AI问题
                      <Info size={11} className="text-violet-400" />
                    </span>
                  </th>
                  <th className="text-left p-3">问题类型</th>
                  <th className="text-left p-3">
                    <span className="inline-flex items-center gap-1.5">
                      <Info size={11} className="text-cyan-400" />
                      热度值
                      <ChevronDown size={10} />
                    </span>
                  </th>
                  <th className="text-left p-3 rounded-r-lg w-[180px]">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                    <td className="p-3">
                      <CheckBox checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} />
                    </td>
                    <td className="p-3 text-white pr-6">{r.question}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] border ${TYPE_COLOR[r.type]}`}>{r.type}</span></td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-violet-500 to-cyan-500" style={{ width: `${Math.min(100, r.heat/10)}%` }} />
                        </div>
                        <span className="font-mono text-gray-200 text-[12px]">{r.heat}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button onClick={()=>setRows(rows.map(x=>x.id===r.id?{...x, saved:!x.saved}:x))}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11.5px] border transition-all ${
                            r.saved ? "bg-amber-500/15 border border-amber-400/40 text-amber-300" : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-amber-300 hover:border-amber-400/40"
                          }`}>
                          {r.saved ? <BookmarkCheck size={11} /> : <Bookmark size={11} />}
                          {r.saved ? "已收藏" : "收藏"}
                        </button>
                        <button onClick={()=>{setRows(rows.map(x=>x.id===r.id?{...x, monitored:!x.monitored}:x)); onAddToMonitor?.();}}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11.5px] border transition-all ${
                            r.monitored ? "bg-cyan-500/15 border border-cyan-400/40 text-cyan-300" : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-cyan-300 hover:border-cyan-400/40"
                          }`}>
                          <PlayCircle size={11} />
                          {r.monitored ? "监测中" : "加入监测"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {selRows.length>0 && (
              <div className="px-4 py-2 border-t border-white/[0.06] flex items-center gap-2 text-[12px] text-gray-500">
                <span>已选 <span className="text-violet-300 font-semibold">{selRows.length}</span> 条，使用右上按钮执行批量操作。</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CheckBox({ checked, onChange }:{ checked:boolean; onChange:()=>void }) {
  return (
    <button onClick={onChange}
      className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
        checked ? "bg-gradient-to-br from-violet-500 to-cyan-500 border-transparent" : "bg-white/[0.04] border border-white/[0.15] hover:border-violet-400/60"
      }`}>
      {checked && (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  );
}
